import useSWR from "swr";
import { officeSourceId, officeSourcePage } from "./office-source";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  PlayerApplication,
  PlayerMode,
  PlayerOptions,
  PlayerPage,
  PlayerPolicy,
  PlayerRegistrationOwner,
} from "src/types/federation-player";
function useScopedKey(url: string | null) {
  const { user } = useAuthContext();
  return url && user?.id ? `${url}#actor-${user.id}` : null;
}
export const playerBase = "/providers/federation-players";
export const registrationBase = (id: number) =>
  `/providers/${id}/federation/player-registration`;
export function usePlayerRegistrationOwner(id: number, enabled = true) {
  return useSWR<PlayerRegistrationOwner>(
    useScopedKey(
      id > 0 && enabled ? `/providers/${id}/player-registration-owner` : null,
    ),
    fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerFederations(enabled: boolean) {
  return useSWR<{ federations: { id: number; label: string }[] }>(
    useScopedKey(enabled ? `${playerBase}/registration-options` : null),
    fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerOptions(id: number, enabled: boolean) {
  return useSWR<PlayerOptions>(
    useScopedKey(id && enabled ? `${registrationBase(id)}/options` : null),
    fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerCapabilities(
  id: number,
  enabled: boolean,
  association = false,
) {
  const key = useScopedKey(
    id && enabled ? `/providers/${id}/federation/players/capabilities` : null,
  );
  return useSWR<{ allowed_actions: string[] }>(
    key ? [key, { params: { association } }] : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function usePlayerApplications(
  mode: PlayerMode,
  id: number,
  enabled: boolean,
  offset = 0,
  stage = "",
  search = "",
  federationProviderId = 0,
  registrationAssociationProviderId = 0,
) {
  const source = mode === "federation" ? officeSourceId("registrations", id) : 0;
  const playerSource = mode === "federation" ? officeSourceId("players", id) : 0;
  const url =
    mode === "player"
      ? `${playerBase}/my-registrations`
      : mode === "association"
      ? `/providers/${id}/federation-player-applications`
      : playerSource ? `/providers/${id}/federation/players/${playerSource}/registration`
      : `/providers/${id}/federation/player-registrations${source ? `/${source}` : ""}`;
  const key = useScopedKey(
    enabled && (mode === "player" || id > 0) ? url : null,
  );
  return useSWR<PlayerPage>(
    key
      ? [
          key,
          {
            params: {
              offset,
              limit: 20,
              ...(stage && { stage }),
              ...(search && { search }),
              ...(mode === "federation" &&
                registrationAssociationProviderId > 0 && {
                  registration_association_provider_id:
                    registrationAssociationProviderId,
                }),
              ...(mode === "player" &&
                federationProviderId > 0 && {
                  federation_provider_id: federationProviderId,
                }),
            },
          },
        ]
      : null,
    source || playerSource ? officeSourcePage : fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerSchema(id: number, enabled: boolean) {
  return useSWR<{ version: number; policy: PlayerPolicy | null }>(
    useScopedKey(id && enabled ? `${registrationBase(id)}/schema` : null),
    fetcher,
    { keepPreviousData: false },
  );
}
export async function playerCommand<T = PlayerApplication>(
  url: string,
  payload: Record<string, unknown>,
  method: "post" | "patch" = "post",
) {
  return (await axios[method]<T>(url, payload)).data;
}
export async function uploadPlayerDocument(
  id: number,
  kind: string,
  file: File,
  revision: number,
  key: string,
) {
  const payload = new FormData();
  payload.append("file", file);
  payload.append("kind", kind);
  payload.append("expected_revision", String(revision));
  payload.append("command_id", key);
  return (
    await axios.post<PlayerApplication>(
      `${playerBase}/my-registrations/${id}/documents`,
      payload,
    )
  ).data;
}
export async function playerDocument(id: number) {
  return (
    await axios.get<Blob>(
      `/providers/federation-player-documents/${id}/content`,
      { responseType: "blob" },
    )
  ).data;
}
export function playerErrorKey(error: unknown) {
  const code = (
    error as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  const raw = (error as { detail?: unknown })?.detail ?? code?.data?.detail;
  const detail = typeof raw === "string" ? raw : "";
  if (detail === "federation_debt_action_blocked") return "debt_action_blocked";
  if (
    detail.includes("permission_denied") ||
    detail.includes("not_found") ||
    detail.includes("self_approval")
  )
    return "fr_denied";
  if (detail.includes("content_erased")) return "fp_erased";
  if (
    detail.includes("foreign_association_setup") ||
    detail.includes("list_snapshot_missing")
  )
    return "fp_association_setup";
  if (detail.includes("setup_required")) return "fr_setup";
  if (
    detail.includes("conflict") ||
    detail.includes("locked") ||
    detail.includes("pending") ||
    detail.includes("schema_changed")
  )
    return "fr_conflict";
  if (Array.isArray(raw)) return "fr_invalid";
  if (detail.includes("citizenship_required")) return "fr_need_citizenship";
  if (detail.includes("photo_required")) return "fr_need_photo";
  if (detail.includes("payment_required")) return "fr_need_payment";
  if (detail.includes("duplicate_review_required")) return "fr_need_duplicate";
  if (
    detail.includes("account_verification_required") ||
    detail.includes("verified_account_required")
  )
    return "fr_need_verification";
  if (
    detail.includes("invalid") ||
    detail.includes("field_") ||
    detail.includes("mismatch") ||
    detail.includes("consent_")
  )
    return "fr_invalid";
  if (code?.status === 401 || code?.status === 403 || code?.status === 404)
    return "fr_denied";
  if (code?.status === 503) return "fr_setup";
  if (code?.status === 409) return "fr_conflict";
  if (code?.status === 422 || code?.status === 413) return "fr_invalid";
  return "fr_error";
}
export async function federationPortalEntry(tag: string) {
  return (
    await axios.get<{
      federation_provider_id: number | null;
      enabled: boolean;
    }>(
      `/providers/public/portals/by-slug/${encodeURIComponent(
        tag,
      )}/federation-player-registration`,
    )
  ).data;
}
export function useFederationPortalEntry(tag?: string) {
  return useSWR<{ federation_provider_id: number | null; enabled: boolean }>(
    tag
      ? `/providers/public/portals/by-slug/${encodeURIComponent(
          tag,
        )}/federation-player-registration`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}

export function usePlayerOptionLists(providerId: number, enabled: boolean) {
  return useSWR<{
    items: import("src/types/federation-player").PlayerOptionList[];
  }>(
    useScopedKey(
      enabled ? `${registrationBase(providerId)}/option-lists` : null,
    ),
    fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerMemberAssociations(
  providerId: number,
  enabled: boolean,
  offset = 0,
) {
  return useSWR<{
    items: import("src/types/federation-player").PlayerMemberAssociation[];
    total: number;
  }>(
    useScopedKey(
      enabled
        ? `${registrationBase(
            providerId,
          )}/member-associations?offset=${offset}&limit=100`
        : null,
    ),
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function usePlayerRetention(providerId: number, enabled: boolean) {
  return useSWR<{
    version: number;
    definition: import("src/types/federation-player").RetentionDefinition;
  }>(
    useScopedKey(enabled ? `${registrationBase(providerId)}/retention` : null),
    fetcher,
    { keepPreviousData: false },
  );
}
export function usePlayerRetentionPreview(
  providerId: number,
  enabled: boolean,
  offset = 0,
) {
  return useSWR<{
    items: import("src/types/federation-player").RetentionCandidate[];
    has_more: boolean;
  }>(
    useScopedKey(
      enabled
        ? `${registrationBase(
            providerId,
          )}/retention/preview?offset=${offset}&limit=100`
        : null,
    ),
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export async function replacePlayerPhoto(
  id: number,
  file: File,
  revision: number,
  key: string,
) {
  const body = new FormData();
  body.append("file", file);
  body.append("command_id", key);
  body.append("expected_revision", String(revision));
  return (
    await axios.post<{
      photo: import("src/types/federation-player").PlayerPhoto;
      player_revision: number;
    }>(`${playerBase}/${id}/photo`, body)
  ).data;
}
export async function playerPhoto(id: number) {
  return (
    await axios.get<Blob>(`/providers/federation-player-photos/${id}/content`, {
      responseType: "blob",
    })
  ).data;
}
export async function exportPlayerRecord(
  providerId: number,
  playerId: number,
  revision: number,
  key: string,
) {
  return (
    await axios.post<Blob>(
      `/providers/${providerId}/federation/players/${playerId}/export`,
      { command_id: key, expected_revision: revision },
      { responseType: "blob" },
    )
  ).data;
}
